import React, { useCallback, useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CurrencyInr, Download } from "@phosphor-icons/react";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function WorkforcePayroll() {
  const [staffList, setStaffList] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/staff");
      setStaffList(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const compute = useCallback(async () => {
    if (staffList.length === 0) return;
    setBusy(true);
    try {
      const out = await Promise.all(staffList.map(s =>
        api.get(`/workforce/staff/${s.id}/payroll?month=${month}`).then(r => r.data)
      ));
      setRows(out);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  }, [staffList, month]);

  useEffect(() => { if (staffList.length) compute(); }, [staffList, month, compute]);

  const totals = rows.reduce((a, r) => ({
    days_worked: a.days_worked + (r.days_worked || 0),
    active_hours: a.active_hours + (r.active_hours || 0),
    ot_hours: a.ot_hours + (r.ot_hours || 0),
    ut_hours: a.ut_hours + (r.ut_hours || 0),
    hours_pay: a.hours_pay + (r.hours_pay || 0),
    base_salary: a.base_salary + (r.base_salary || 0),
    incentive: a.incentive + (r.incentive || 0),
    gross: a.gross + (r.gross || 0),
  }), { days_worked: 0, active_hours: 0, ot_hours: 0, ut_hours: 0, hours_pay: 0, base_salary: 0, incentive: 0, gross: 0 });

  const exportCsv = () => {
    const header = ["Staff", "Code", "Days", "Expected Hours", "Active Hours", "OT Hours", "UT Hours", "Hourly Rate", "Hours Pay", "OT Bonus", "Base Salary", "Tasks Done", "Incentive", "Gross"];
    const lines = [header.join(",")];
    rows.forEach(r => {
      lines.push([r.name, r.staff_code || "", r.days_worked || 0, r.expected_hours || 0, r.active_hours, r.ot_hours || 0, r.ut_hours || 0, r.hourly_rate, r.hours_pay, r.ot_bonus || 0, r.base_salary, r.tasks_done, r.incentive, r.gross].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `payroll_${month}.csv`;
    a.click();
  };

  return (
    <AppShell title="Payroll" subtitle={`Month: ${month}`}
      actions={
        <div className="flex gap-2">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                 data-testid="payroll-month"
                 className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          <button onClick={exportCsv} data-testid="payroll-export"
                  className="inline-flex items-center gap-2 h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">
            <Download size={14}/>Export CSV
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-6 gap-3 mb-4" data-testid="payroll-totals">
        <Stat label="Days" value={totals.days_worked}/>
        <Stat label="Active Hours" value={totals.active_hours.toFixed(1)}/>
        <Stat label="OT Hours" value={totals.ot_hours.toFixed(1)}/>
        <Stat label="UT Hours" value={totals.ut_hours.toFixed(1)}/>
        <Stat label="Incentive" value={`₹${totals.incentive.toFixed(0)}`}/>
        <Stat label="Gross Total" value={`₹${totals.gross.toFixed(0)}`} highlight/>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="payroll-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-4 py-3">Staff</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3 text-right">Days</th>
              <th className="px-4 py-3 text-right">Exp. Hrs</th>
              <th className="px-4 py-3 text-right">Active Hrs</th>
              <th className="px-4 py-3 text-right text-[#2D5A27]">OT</th>
              <th className="px-4 py-3 text-right text-[#C25D38]">UT</th>
              <th className="px-4 py-3 text-right">₹/hr</th>
              <th className="px-4 py-3 text-right">Hours Pay</th>
              <th className="px-4 py-3 text-right">OT Bonus</th>
              <th className="px-4 py-3 text-right">Base</th>
              <th className="px-4 py-3 text-right">Tasks</th>
              <th className="px-4 py-3 text-right">Incentive</th>
              <th className="px-4 py-3 text-right">Gross</th>
            </tr>
          </thead>
          <tbody>
            {busy && <tr><td colSpan={14} className="px-6 py-8 text-center text-[#8A8780]">Computing…</td></tr>}
            {!busy && rows.length === 0 && <tr><td colSpan={14} className="px-6 py-12 text-center text-[#8A8780]">No data</td></tr>}
            {!busy && rows.map((r, i) => (
              <tr key={r.staff_id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.staff_code || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{r.days_worked || 0}</td>
                <td className="px-4 py-3 text-right font-mono text-[#8A8780]">{Number(r.expected_hours || 0).toFixed(1)}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(r.active_hours || 0).toFixed(1)}</td>
                <td className="px-4 py-3 text-right font-mono text-[#2D5A27]">{Number(r.ot_hours || 0).toFixed(1)}</td>
                <td className="px-4 py-3 text-right font-mono text-[#C25D38]">{Number(r.ut_hours || 0).toFixed(1)}</td>
                <td className="px-4 py-3 text-right font-mono">{r.hourly_rate}</td>
                <td className="px-4 py-3 text-right font-mono">₹{Number(r.hours_pay || 0).toFixed(0)}</td>
                <td className="px-4 py-3 text-right font-mono text-[#2D5A27]">₹{Number(r.ot_bonus || 0).toFixed(0)}</td>
                <td className="px-4 py-3 text-right font-mono">₹{Number(r.base_salary || 0).toFixed(0)}</td>
                <td className="px-4 py-3 text-right font-mono">{r.tasks_done}</td>
                <td className="px-4 py-3 text-right font-mono">₹{Number(r.incentive || 0).toFixed(0)}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-[#2D5A27]">₹{Number(r.gross || 0).toFixed(0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}

const Stat = ({ label, value, highlight }) => (
  <div className={`rounded-md p-4 border ${highlight ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white border-[#E5E2D9]"}`}>
    <div className="text-[10px] uppercase tracking-[0.2em] opacity-70 flex items-center gap-1"><CurrencyInr size={12}/>{label}</div>
    <div className="font-heading text-2xl mt-1">{value}</div>
  </div>
);
